<template>
  <div class="language-switcher">
    <!-- 统一使用桌面版语言切换样式 -->
    <div class="language-switcher-desktop">
      <el-dropdown 
        trigger="click" 
        placement="bottom-end"
        @command="handleLanguageChange"
        :hide-on-click="true"
        :popper-append-to-body="true"
        popper-class="language-dropdown-popper"
      >
        <span class="current-language" :class="{ 'compact-style': isCompact }">
          <span style="margin-right: 10px;" class="fi" :class="`fi-${currentLanguage.code}`"></span>
          <span v-if="!isCompact" class="text">{{ currentLanguage.name }}</span>
          <i class="el-icon-arrow-down el-icon--right"></i>
        </span>
        <el-dropdown-menu slot="dropdown" class="language-dropdown">
          <el-dropdown-item 
            v-for="lang in availableLanguages" 
            :key="lang.country"
            :command="lang.country"
            :class="{ 'is-active': lang.country === currentLanguage.country }"
          >
            <span style="margin-right: 10px;"  class="fi" :class="`fi-${lang.code}`"></span>
            <span class="text">{{ lang.name }}</span>
            <i v-if="lang.country === currentLanguage.country" class="el-icon-check"></i>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 加载状态指示器 -->
    <transition name="fade">
      <div v-if="isLoading" class="loading-indicator">
        <i class="el-icon-loading"></i>
      </div>
    </transition>
  </div>
</template>

<script>
import i18nMonitorMixin from '@/mixins/i18n-monitor-mixin'
import { setI18nLanguage } from '@/lang/index'

export default {
  name: 'LanguageSwitcher',
  mixins: [i18nMonitorMixin],
  
  props: {
    // 是否使用紧凑模式
    isCompact: {
      type: Boolean,
      default: false
    },
    // 是否在切换后刷新页面请求
    refreshOnChange: {
      type: Boolean,
      default: true
    },
    // 自定义样式类名
    customClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isLoading: false,
      // 阻止重复切换的标志
      switchingLanguage: false,
      // 可用的语言列表
      availableLanguages: [
        { country: 'en', name: 'English', code: 'gb' },
        { country: 'cn', name: '中文简体', code: 'cn' },
        { country: 'by', name: 'Русский', code: 'ru' },
        { country: 'es', name: 'Español', code: 'es' },
        { country: 'fr', name: 'Français', code: 'fr' },
        { country: 'de', name: 'Deutsch', code: 'de' },
        { country: 'it', name: 'italiano', code: 'it' },
        
      ]
    }
  },

  computed: {
    currentLanguage() {
      const lang = this.$store.getters['user/currentLanguage']
      // 确保总是返回有效的语言对象，包含code属性
      if (lang && lang.country && lang.name) {
        // 查找对应语言的code属性并添加
        const matchingLang = this.availableLanguages.find(l => l.country === lang.country)
        if (matchingLang && matchingLang.code) {
          return { ...lang, code: matchingLang.code }
        }
        return lang
      }
      // 如果没有有效语言对象，返回默认语言
      return this.availableLanguages[0]
    }
  },
  
  watch: {
    // 监听语言状态变化，确保组件及时更新
    currentLanguage: {
      handler(newLang, oldLang) {
        if (newLang && oldLang && newLang.country !== oldLang.country) {
          // 强制重新渲染以确保显示更新
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
      },
      deep: true
    }
  },
  
  mounted() {
    // 添加调试信息
    
    // 监听 Vuex 状态变化
    this.$store.watch(
      state => state.user.language,
      (newLang, oldLang) => {
        this.$forceUpdate()
      },
      { deep: true }
    )
  },

  methods: {
    // 处理语言切换
    async handleLanguageChange(countryCode) {
      // 根据country code找到对应的语言对象
      const languageObj = this.availableLanguages.find(lang => lang.country === countryCode)
      
      if (!languageObj) {
        console.error('Invalid language code:', countryCode)
        return
      }
      
      // 防止重复切换
      if (this.switchingLanguage || this.isLoading) {
        return
      }

      // 如果切换到相同语言，直接返回
      if (languageObj.country === this.currentLanguage.country) {
        return
      }

      // 保存当前语言用于回滚和事件触发
      const previousLanguage = { ...this.currentLanguage }
      
      this.isLoading = true
      this.switchingLanguage = true

      try {

        // 1. 更新 Vuex 状态
        await this.$store.dispatch('user/toggleLanguage', languageObj)

        // 2. 更新 vue-i18n
        setI18nLanguage(languageObj.country)

        // 3. 更新 HTML lang 属性
        document.documentElement.setAttribute('lang', languageObj.country)

        // 4. 等待状态更新完成
        await this.$nextTick()
        
        // 5. 强制更新组件以确保显示正确
        this.$forceUpdate()
        
        // 6. 再次等待DOM更新
        await this.$nextTick()

        // 7. 使用路由助手切换语言（处理URL）
        const switchResult = await this.$switchLanguage(languageObj.country)

        if (switchResult.success) {
          // 8. 刷新当前页面的所有请求（如果启用）
          if (this.refreshOnChange) {
            await this.refreshPageRequests()
          }

          // 9. 触发全局事件（使用保存的previous和新的language对象）
          this.$emit('language-changed', {
            from: previousLanguage.country,
            to: languageObj.country,
            timestamp: Date.now()
          })

          // 全局事件总线通知
          if (this.$eventBus) {
            this.$eventBus.$emit('language-switched', {
              from: switchResult.from || previousLanguage.country,
              to: switchResult.to || languageObj.country,
              language: languageObj
            })
          }

        //   this.$message.success(`语言已切换到 ${languageObj.name}`)
        } else {
          throw new Error('Route switch failed')
        }

      } catch (error) {
        console.error('❌ Language switch failed:', error)
        this.$message.error('Language switch failed')
        
        // 回滚状态
        try {
          await this.$store.dispatch('user/toggleLanguage', previousLanguage)
          setI18nLanguage(previousLanguage.country)
          document.documentElement.setAttribute('lang', previousLanguage.country)
        } catch (rollbackError) {
          console.error('❌ Failed to rollback language state:', rollbackError)
        }
      } finally {
        this.isLoading = false
        // 延迟重置切换标志，避免连续点击
        setTimeout(() => {
          this.switchingLanguage = false
        }, 500)
      }
    },

    // 刷新当前页面的所有请求
    async refreshPageRequests() {
      try {
        // 触发父组件或页面的刷新方法
        this.$emit('refresh-requests')

        // 如果当前组件有刷新方法，调用它
        if (this.$parent && typeof this.$parent.refreshData === 'function') {
          await this.$parent.refreshData()
        }

        // 触发全局刷新事件
        if (this.$eventBus) {
          this.$eventBus.$emit('refresh-page-data', {
            language: this.currentLanguage.country,
            timestamp: Date.now()
          })
        }

        // 查找页面中所有的 Vue 组件并触发刷新
        this.triggerComponentsRefresh()

      } catch (error) {
        console.error('Failed to refresh page requests:', error)
      }
    },

    // 触发组件刷新
    triggerComponentsRefresh() {
      // 递归查找所有子组件并触发刷新
      const refreshComponent = (component) => {
        if (component && component.$children) {
          component.$children.forEach(child => {
            // 如果组件有 refreshData 方法，调用它
            if (typeof child.refreshData === 'function') {
              try {
                child.refreshData()
              } catch (error) {
                console.warn('Component refresh failed:', error)
              }
            }
            
            // 如果组件有 getList 方法（常见的数据获取方法），调用它
            if (typeof child.getList === 'function') {
              try {
                child.getList()
              } catch (error) {
                console.warn('Component getList failed:', error)
              }
            }

            // 递归处理子组件
            refreshComponent(child)
          })
        }
      }

      // 从根实例开始刷新
      if (this.$root) {
        refreshComponent(this.$root)
      }
    },

    // 获取语言显示名称
    getLanguageName(countryCode) {
      const lang = this.availableLanguages.find(l => l.country === countryCode)
      return lang ? lang.name : countryCode.toUpperCase()
    }
  }
}
</script>

<style lang="less" scoped>
.language-switcher {
  position: relative;
  display: inline-block;

  .language-switcher-desktop {
    .current-language {
      display: flex;
      align-items: center;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 4px;
      transition: all 0.3s ease;
      color: white;
      font-size: 14px;
      margin-right: 8px;
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }

      // 紧凑样式（用于空间不足的情况）
      &.compact-style {
        padding: 4px 8px;
        font-size: 12px;
        
        .flag {
          width: 16px;
          height: 12px;
          margin-right: 4px;
        }
        
        .text {
          display: none; // 紧凑模式下隐藏文字
        }
      }

      .flag {
        width: 20px;
        height: 15px;
        margin-right: 8px;
        border-radius: 2px;
        background-size: cover;
        background-position: center;
        display: inline-block;
      }

      .text {
        margin-right: 4px;
      }
    }
  }

  .loading-indicator {
    position: absolute;
    top: 50%;
    right: -30px;
    transform: translateY(-50%);
    color: #409eff;
    font-size: 16px;
  }
}

// 语言下拉菜单样式
/deep/ .language-dropdown {
  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    padding: 8px 16px;

    .flag {
      width: 20px;
      height: 15px;
      margin-right: 8px;
      border-radius: 2px;
      background-size: cover;
      background-position: center;
      display: inline-block;
    }

    .text {
      flex: 1;
    }

    &.is-active {
      background-color: #f5f7fa;
      color: #409eff;
      font-weight: 500;
    }

    .el-icon-check {
      margin-left: 8px;
      color: #409eff;
    }
  }
}

// 固定定位的下拉框样式
/deep/ .language-dropdown-popper {
  position: fixed !important;
  z-index: 9999 !important;
  transform: none !important;
  will-change: auto !important;
}

// 国旗样式
.flag-en {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjE1IiBmaWxsPSIjMDEyMzY5Ii8+CjxwYXRoIGQ9Ik0wIDEuODc1SDIwVjMuMTI1SDBWMS44NzVaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K');
}

.flag-cn {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjE1IiBmaWxsPSIjREUyOTEwIi8+Cjwvc3ZnPgo=');
}

.flag-ru, .flag-by {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjUiIGZpbGw9IndoaXRlIi8+CjxyZWN0IHk9IjUiIHdpZHRoPSIyMCIgaGVpZ2h0PSI1IiBmaWxsPSIjMDA1MkI0Ii8+CjxyZWN0IHk9IjEwIiB3aWR0aD0iMjAiIGhlaWdodD0iNSIgZmlsbD0iI0QwMjAzMCIvPgo8L3N2Zz4K');
}

.flag-es {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjE1IiBmaWxsPSIjRkZEOTAwIi8+CjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIzIiBmaWxsPSIjQzYwQjFFIi8+CjxyZWN0IHk9IjEyIiB3aWR0aD0iMjAiIGhlaWdodD0iMyIgZmlsbD0iI0M2MEIxRSIvPgo8L3N2Zz4K');
}

.flag-fr {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYuNjciIGhlaWdodD0iMTUiIGZpbGw9IiMwMDJBOEYiLz4KPHJlY3QgeD0iNi42NyIgd2lkdGg9IjYuNjciIGhlaWdodD0iMTUiIGZpbGw9IndoaXRlIi8+CjxyZWN0IHg9IjEzLjMzIiB3aWR0aD0iNi42NyIgaGVpZ2h0PSIxNSIgZmlsbD0iI0VGMzA0RCIvPgo8L3N2Zz4K');
}

// 淡入淡出动画
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>